import { UserCheck, XCircle, Calendar, Send, CheckCircle } from 'lucide-react'
import { userHasPermission } from '@/shared/lib/roleUtils'
import type { Candidate } from '@/services/api'
import { puedeAgendarEntrevistaDelSolicitante } from '@/features/rh/lib/agendaDelSolicitante'

/**
 * Las columnas del tablero, como datos.
 *
 * `accionDeEntrada` es la acción que lleva un candidato a esa columna, y es la
 * que se dispara al soltarlo ahí. Que exista aquí no significa que cualquiera
 * pueda hacerla: el arrastre consulta las mismas reglas de permisos que el menú
 * de la tarjeta, así que soltar nunca logra algo que el menú no ofrezca.
 *
 * Sin revisión no tiene acción de entrada: no hay manera de devolver un
 * candidato al principio del proceso, y por eso tampoco acepta que se le suelte
 * nada.
 */
export interface ColumnaDef {
  clave: 'sin_revision' | 'en_proceso' | 'entrevista_rh' | 'pre_seleccionado' | 'seleccionado'
  titulo: string
  /** Fondo del contenedor del título. */
  color: string
  /** Contador de la columna, arriba en el encabezado. */
  badge: { texto: string; fondo: string }
  /**
   * Pastilla de estado al pie de cada tarjeta. Lleva colores distintos a los
   * del contador. Sin revisión no tiene: una tarjeta que nadie ha tocado no
   * necesita anunciar su estado.
   */
  /**
   * Colores de la pastilla de estatus. El tablero ya no la pinta —repetia el
   * titulo de la columna en la que ya esta la tarjeta—, pero `estiloDeEstatus`
   * los sigue usando para las pastillas de los listados.
   */
  pastilla?: { texto: string; fondo: string }
  accionDeEntrada?: string
}

export const COLUMNAS: readonly ColumnaDef[] = [
  {
    clave: 'sin_revision',
    titulo: 'Sin revisión',
    color: '#FFFFFF',
    badge: { texto: '#315FB9', fondo: '#EDFAFE' },
  },
  {
    clave: 'en_proceso',
    titulo: 'En proceso',
    color: '#FEF6E2',
    badge: { texto: '#795E1B', fondo: '#FFE8AD' },
    pastilla: { texto: '#D4A112', fondo: '#FEF6E2' },
    accionDeEntrada: 'preselect',
  },
  {
    clave: 'entrevista_rh',
    titulo: 'Entrevista RH',
    color: '#EDF2FE',
    badge: { texto: '#2563EB', fondo: '#CFDDFF' },
    pastilla: { texto: '#2563EB', fondo: '#EAF0FDE5' },
    accionDeEntrada: 'schedule_interview',
  },
  {
    clave: 'pre_seleccionado',
    titulo: 'Aprobado por RH',
    color: '#EAF8FE',
    badge: { texto: '#25AADA', fondo: '#BEEBFF' },
    pastilla: { texto: '#17B3EB', fondo: '#E2F4FC' },
    accionDeEntrada: 'send_manager',
  },
  {
    clave: 'seleccionado',
    titulo: 'Seleccionado',
    color: '#1ACE5C12',
    badge: { texto: '#0E8038', fondo: '#C1EED2' },
    pastilla: { texto: '#1ACE5C', fondo: '#F4F7F9' },
    // Sin accionDeEntrada a propósito (RH-03.1): soltar aquí disparaba
    // aprobar, que deja al candidato en pendiente_cierre. Aquí viven
    // todos los que el solicitante ya eligió: por cerrar, con Dirección y
    // cerrados. Se cierran desde el menú de la tarjeta, nunca arrastrando.
  },
]

export type ClaveColumna = ColumnaDef['clave']
export type Pestana = 'activos' | 'descartados' | 'historial'

/**
 * Acciones que un usuario puede ejecutar sobre un candidato según su estado.
 *
 * Vive fuera del componente porque la usan dos caminos: el menú de la tarjeta y
 * el arrastre entre columnas. Con una sola lista, soltar una tarjeta no puede
 * lograr algo que el menú no ofrezca — si estuviera duplicada, el arrastre se
 * convertiría en una puerta trasera a los permisos.
 */
/** El mismo permiso que abre la pantalla de Cierre y selección final. */
const PERMISO_DE_CIERRE = 'rh.reclutamiento_candidatos.especial.cerrar_seleccion'

/** Las acciones de tarjeta que cierran el proceso, y qué decisión de cierre son. */
export const ACCION_DE_CIERRE_DE_TARJETA: Record<string, 'seleccionar' | 'enviar_direccion'> = {
  cierre_seleccionar: 'seleccionar',
  cierre_enviar_direccion: 'enviar_direccion',
}

export const accionesDisponibles = (
status: Candidate['status'],
permisos: string[],
) => {
const canRequesterFinalDecision = userHasPermission(
  'rh.solicitudes_personal.base.create',
  permisos,
)

  const actions: Array<{ id: string; label: string; icon: React.ReactNode }> = []
  
  switch (status) {
    case 'pre_registro':
    case 'registrado':
    case 'pendiente':
      if(userHasPermission("rh.reclutamiento_candidatos.especial.change_status", permisos)) {
        actions.push(
          { id: 'preselect', label: 'En proceso', icon: <UserCheck className="w-4 h-4" /> },
        )
      if(userHasPermission("rh.reclutamiento_candidatos.especial.change_status", permisos)) {
        actions.push(
          { id: 'reject', label: 'Descartar', icon: <XCircle className="w-4 h-4" /> }
        )
      }
      }
      break
    case 'en_proceso':
      // Solo permitir agendar entrevista en la columna de preseleccionados
      if(
        userHasPermission("rh.entrevistas.base.create", permisos) ||
        userHasPermission("rh.entrevistas.base.create", permisos)
      ) {
        actions.push({ id: 'schedule_interview', label: 'Agendar Entrevista', icon: <Calendar className="w-4 h-4" /> })
      }
      // Descartar faltaba solo en esta etapa: en las demás columnas activas sí
      // estaba, y no hay regla que lo prohíba aquí. Mismo permiso que el resto.
      if (userHasPermission("rh.reclutamiento_candidatos.especial.change_status", permisos)) {
        actions.push({ id: 'reject', label: 'Descartar', icon: <XCircle className="w-4 h-4" /> })
      }
      break
    case 'entrevista_rh':
      if(userHasPermission("rh.reclutamiento_candidatos.especial.change_status", permisos)) {
        actions.push({ id: 'send_manager', label: 'Enviar a jefe', icon: <Send className="w-4 h-4" /> })
      }
      if(userHasPermission("rh.reclutamiento_candidatos.especial.change_status", permisos)) {
        actions.push({ id: 'reject', label: 'Descartar', icon: <XCircle className="w-4 h-4" /> })
      }
      break
    case 'pre_seleccionado':
    case 'entrevista_solicitante':
      // SRH-308: RH y administración agendan la entrevista del solicitante en
      // su nombre. Antes esta columna solo ofrecía acciones a quien crea
      // solicitudes, y una cuenta de RH no tenía ninguna aquí: el candidato se
      // quedaba esperando a que el solicitante agendara.
      if (puedeAgendarEntrevistaDelSolicitante(permisos)) {
        actions.push({
          id: 'schedule_requester_interview',
          label: 'Agendar entrevista del solicitante',
          icon: <Calendar className="w-4 h-4" />,
        })
      }
      // «Aprobar» ya no se ofrece aquí. Con entrevista final, el solicitante
      // aprueba en la pantalla de entrevistas. Sin ella decide RH: aprobar los
      // resultados de su entrevista lleva al candidato directo al cierre, así
      // que el solicitante no tiene una aprobación pendiente.
      if (canRequesterFinalDecision) {
        actions.push({ id: 'reject', label: 'Descartar', icon: <XCircle className="w-4 h-4" /> })
      }
      break
    case 'pendiente_cierre':
      // Las mismas decisiones que la pantalla de Cierre. RH no rechaza ni
      // devuelve aquí: el solicitante ya aprobó.
      if (userHasPermission(PERMISO_DE_CIERRE, permisos)) {
        actions.push(
          { id: 'cierre_seleccionar', label: 'Seleccionar', icon: <CheckCircle className="w-4 h-4" /> },
          { id: 'cierre_enviar_direccion', label: 'Enviar a Dirección', icon: <Send className="w-4 h-4" /> },
        )
      }
      break
    case 'decision_direccion':
      // Seleccionar o descartar depende de lo que Dirección decidió, y el
      // tablero no trae esa decisión: se resuelve al elegir la acción.
      if (userHasPermission(PERMISO_DE_CIERRE, permisos)) {
        actions.push({
          id: 'cierre_confirmar',
          label: 'Confirmar decisión de Dirección',
          icon: <CheckCircle className="w-4 h-4" />,
        })
      }
      break
    case 'seleccionado':
      if (userHasPermission("rh.empleados_preregistro.base.update", permisos)) {
        actions.push({ id: 'start_file', label: 'Iniciar expediente', icon: <UserCheck className="w-4 h-4" /> })
      }
      break
  }
  
  return actions
}

/**
 * Lo que muestra el contador de una columna.
 *
 * A partir de 100 se corta en "99+": el contador es un cuadro de 28px y un
 * número de tres o más dígitos lo deformaría.
 */
/**
 * Pastilla al pie de una tarjeta cuando su estatus pide una propia.
 *
 * La de la columna sirve mientras todas sus tarjetas significan lo mismo. Con
 * RH-03.1 dejó de ser cierto: `pendiente_cierre`, `pendiente_direccion` y
 * `decision_direccion` viven en la columna de «Seleccionado» pero están en
 * momentos distintos del cierre.
 */
export const pastillaDeTarjeta = (
  status: string,
): { texto: string; fondo: string; etiqueta: string } | null => {
  switch (status) {
    case 'pendiente_cierre':
      return { etiqueta: 'Por cerrar (RH)', texto: '#795E1B', fondo: '#FEF6E2' }
    case 'pendiente_direccion':
      return { etiqueta: 'En validación de Dirección', texto: '#6B21A8', fondo: '#F3E8FF' }
    case 'decision_direccion':
      return { etiqueta: 'Decisión de Dirección (RH confirma)', texto: '#5B21B6', fondo: '#EDE9FE' }
    default:
      return null
  }
}

export const etiquetaDeConteo = (count: number): string =>
  count > 99 ? '99+' : String(count)

/**
 * Filtro del buscador del tablero.
 *
 * Compara sin acentos y sin distinguir mayúsculas: buscar "ramirez" tiene que
 * encontrar a "Ramírez", que es como la gente escribe cuando va de prisa.
 * Busca en el nombre y en la vacante, que son los dos datos visibles de la
 * tarjeta por los que alguien buscaría.
 */
const sinAcentos = (texto: string): string =>
  texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()

export const coincideBusqueda = (
  candidate: Pick<Candidate, 'fullName'> & {
    jobRequest?: { puestoSolicitar?: string | null } | null
  },
  busqueda: string,
): boolean => {
  const termino = sinAcentos(busqueda)
  if (!termino) return true
  const campos = [candidate.fullName, candidate.jobRequest?.puestoSolicitar ?? '']
  return campos.some((campo) => sinAcentos(String(campo)).includes(termino))
}

/**
 * Nombre de la etapa de la que salió un candidato al descartarlo.
 *
 * El servidor guarda el estatus crudo; la tabla muestra el nombre de la columna
 * del tablero, que es como la gente se refiere a las etapas.
 */
const ETAPAS: Record<string, string> = {
  pre_registro: 'Sin revisión',
  registrado: 'Sin revisión',
  pendiente: 'Sin revisión',
  en_proceso: 'En proceso',
  entrevista_rh: 'Entrevista RH',
  pre_seleccionado: 'Aprobado por RH',
  entrevista_solicitante: 'Aprobado por RH',
  // Viven en la columna de Seleccionado, con su propio badge (RH-03.1): el
  // solicitante ya los eligió y falta que RH cierre.
  pendiente_cierre: 'Seleccionado',
  pendiente_direccion: 'Seleccionado',
  decision_direccion: 'Seleccionado',
  seleccionado: 'Seleccionado',
}

export const etiquetaDeEtapa = (status?: string | null): string =>
  (status && ETAPAS[status]) || '------'

/**
 * Motivos de descarte.
 *
 * Provisionales: el catálogo definitivo no está decidido. Viven aquí y el
 * servidor los guarda como texto libre, así que cambiar esta lista no pide
 * migración ni despliegue del back.
 */
export const MOTIVOS_DESCARTE = [
  'No cumple el perfil',
  'Expectativa salarial fuera de rango',
  'No se presentó a la entrevista',
  'Rechazó la oferta',
  'Vacante cubierta por otro candidato',
] as const

/**
 * Nombre de quien descartó. Devuelve el guion cuando no hay usuario o cuando
 * viene sin nombre, para que la tabla y el modal digan lo mismo.
 */
export const nombreDeQuienDescarto = (
  usuario?: { firstName?: string; lastName?: string } | null,
): string =>
  usuario ? `${usuario.firstName ?? ''} ${usuario.lastName ?? ''}`.trim() || '------' : '------'

/**
 * Convierte la fecha que manda el servidor en un momento real.
 *
 * El servidor la manda en UTC pero sin la Z al final, y sin esa marca el
 * navegador la lee como hora local: un descarte de las 5:31 p.m se mostraba a
 * las 10:31 p.m. Se agrega la marca cuando la cadena trae hora y no trae zona.
 * Las fechas sin hora se dejan tal cual: ahí el navegador ya asume UTC.
 */
const aMomento = (iso?: string | null): Date | null => {
  if (!iso) return null

  const traeHora = /\d{2}:\d{2}/.test(iso)
  const traeZona = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso)
  const momento = new Date(traeHora && !traeZona ? `${iso.replace(' ', 'T')}Z` : iso)

  return Number.isNaN(momento.getTime()) ? null : momento
}

/** Solo la fecha, para la columna del listado de descartados. */
export const fechaDeDescarte = (iso?: string | null): string =>
  aMomento(iso)?.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }) ?? '------'

/**
 * Fecha y hora del descarte como las pide el diseño: 19/08/2026, 4:00 p.m
 *
 * Se arma a mano porque `toLocaleString` escribe el meridiano como "p. m."
 * —con espacio y dos puntos— y el diseño lo lleva sin ellos.
 */
export const fechaHoraDeDescarte = (iso?: string | null): string => {
  const momento = aMomento(iso)
  if (!momento) return '------'

  const dia = String(momento.getDate()).padStart(2, '0')
  const mes = String(momento.getMonth() + 1).padStart(2, '0')
  const hora24 = momento.getHours()
  const hora = hora24 % 12 === 0 ? 12 : hora24 % 12
  const minutos = String(momento.getMinutes()).padStart(2, '0')

  return `${dia}/${mes}/${momento.getFullYear()}, ${hora}:${minutos} ${hora24 < 12 ? 'a.m' : 'p.m'}`
}

/**
 * Etiqueta de una vacante en el select de devolver al tablero.
 *
 * Lleva el id por delante porque hay vacantes con el mismo puesto y la misma
 * área: sin él, varias opciones se ven idénticas y elegir es adivinar. Es el
 * mismo `#id` con el que ya se identifica una vacante en su pantalla de detalle.
 */
export const etiquetaDeVacante = (vacante: {
  id: number
  puestoSolicitar?: string | null
  areaVacante?: string | null
}): string =>
  [`#${vacante.id}`, vacante.puestoSolicitar, vacante.areaVacante]
    .filter(Boolean)
    .join(' · ')

/**
 * Si la tarjeta debe anunciar que la vacante pide entrevista del solicitante.
 *
 * Solo tiene sentido decirlo mientras el candidato está esperando esa
 * decisión: antes no aplica, y después ya pasó. Es lo que explica que en esa
 * etapa no aparezca «Aprobar» en el menú de la tarjeta.
 */
export const pideEntrevistaDelSolicitante = (candidato: {
  status?: string | null
  jobRequest?: { entrevistaFinalSolicitante?: boolean } | null
}): boolean =>
  candidato.status === 'pre_seleccionado' &&
  candidato.jobRequest?.entrevistaFinalSolicitante === true
